import { RestClient, RestError } from "./restClient";

/** Auth mode for a user-added MCP server.
 *  - "none"       : the server needs no credential (e.g. Microsoft Learn,
 *                   DeepWiki) — tools list/call directly, nothing to configure.
 *  - "static"     : a pasted bearer/header/basic credential (encrypted server-side)
 *  - "user_oauth" : the standard connector OAuth dance (per-user token vault)
 *  - "mcp_oauth"  : MCP-native OAuth — paste only a URL; the server's own OAuth
 *                   (metadata discovery + dynamic client registration) is
 *                   negotiated lazily on first connect.
 */
export type McpAuthMode = "none" | "static" | "user_oauth" | "mcp_oauth";

export type McpTransport = "streamable_http" | "sse";

export type StaticSecretMode = "bearer" | "header" | "basic" | "custom_headers";

/** Static-auth block. Secrets are WRITE-ONLY — sent on create, never returned by
 *  any GET (the backend redacts them).
 *
 *  - bearer/header/basic: a single `secret` string.
 *  - custom_headers: a generic multi-header mode. The caller supplies two header
 *    maps — `plain` (non-sensitive, e.g. a target URL) and `encrypted`
 *    (sensitive values, encrypted at rest). Any header names are allowed; the
 *    connector emits `plain ∪ encrypted` per request. (The backend also accepts
 *    a Superset convenience preset via `superset_*`, but the UI sends the
 *    generic maps.) */
export interface StaticAuthInput {
  mode: StaticSecretMode;
  /** Required when mode === "header" (the header name to inject). */
  header_name?: string;
  /** Required for bearer/header/basic. */
  secret?: string;
  /** custom_headers — non-sensitive headers (stored/readable as-is). */
  plain?: Record<string, string>;
  /** custom_headers — sensitive headers (values encrypted at rest, write-only). */
  encrypted?: Record<string, string>;
  /** Per-user install: create the connector with wiring (mode + header_name)
   *  but no shared secret. Each end-user attaches their own token via
   *  POST /plugins/v1/connectors/{slug}/secret with their own userJWT.
   *  Backend refuses `per_user=true` when `secret` is present. */
  per_user?: boolean;
}

/** OAuth block for user_oauth mode — either inline provider creds or a
 *  reference to an existing oauth_providers slug (handled server-side). */
export interface OAuthInput {
  auth_url: string;
  token_url: string;
  client_id: string;
  client_secret: string;
  scopes: string[];
}

export interface AddMcpInput {
  // Optional: sent only when EDITING (the immutable server-generated id). On
  // add it is omitted and the backend mints an opaque `c_<uuid>` identity.
  name?: string;
  display_name: string;
  description?: string;
  mcp_url: string;
  transport?: McpTransport;
  auth_mode: McpAuthMode;
  /** Present when auth_mode === "static". */
  static_auth?: StaticAuthInput;
  /** Present when auth_mode === "user_oauth". */
  oauth?: OAuthInput;
  /** Optional at add-time; can be set later via saveSkills(). */
  curated_tools?: string[];
}

/** A tool discovered from the MCP server's tools/list. category / read_only /
 *  title / tags come from the tool's annotations or _meta; they are absent for
 *  servers that send none and for cache rows written before the probe carried
 *  them. The Manage-tools dialog then groups by connector (or "Other") and
 *  reads read/write from the leading verb — never from per-connector rules. */
export interface DiscoveredTool {
  upstream_name: string;
  description: string;
  input_schema: Record<string, unknown>;
  category?: string;
  read_only?: boolean;
  title?: string;
  /** The server's tags (a tool can carry several); searchable as `tag:<name>`. */
  tags?: string[];
}

export interface DiscoverResponse {
  server_up: boolean;
  /** Present for user_oauth when the user hasn't connected yet. */
  needs_auth?: boolean;
  /** Present when needs_auth — open this to run the OAuth dance. */
  authorize_url?: string;
  tools: DiscoveredTool[];
}

/** A row in the custom-MCP list. NEVER contains secrets. */
export interface CustomMcpListing {
  name: string;
  display_name: string;
  enabled: boolean;
  auth_mode: McpAuthMode;
  mcp_url: string;
  /** Non-secret static-auth shape — lets the edit UI restore the right mode
   *  (e.g. the X-Client-Credentials header ⇒ client-credentials). Never a secret. */
  static_mode?: StaticSecretMode | null;
  static_header_name?: string | null;
  curated_tools: string[];
  discovered_count: number;
  /** True when the connector is usable now: a live OAuth token exists for
   *  oauth-family modes, or the mode needs no sign-in (static/none). Drives the
   *  "Connected" chip and whether the Connect action is offered. */
  connected: boolean;
}

/** Cached discovered tools + current curated allowlist for the Manage-tools
 *  editor (GET /custom/{name}/tools). No secret, no live probe. */
export interface CustomMcpTools {
  discovered: DiscoveredTool[];
  curated: string[];
  discovered_at_least_once: boolean;
}

/** Which surface to target: company-wide (admin) or the caller's personal MCPs. */
export type McpScope = "company" | "user";

/** Wraps the plugin_platform_service custom-MCP surface. Company scope lives
 *  under /plugins/v1/connectors/custom (admin); per-user scope under
 *  /plugins/v1/mcp. Both share the same request/response shapes, so the scope
 *  just selects the prefix. Listing tolerates 404 (feature flag off / not yet
 *  deployed) by returning []. Methods throw RestError on other non-2xx. */
export class CustomMcpApi {
  constructor(private client: RestClient) {}

  private base(scope: McpScope): string {
    return scope === "company" ? "/plugins/v1/connectors/custom" : "/plugins/v1/mcp";
  }

  /** List the custom MCP connectors for a scope. [] on 404. */
  async list(scope: McpScope): Promise<CustomMcpListing[]> {
    try {
      // Company list endpoint is GET /connectors/custom; user is GET /mcp.
      const r = await this.client.get<CustomMcpListing[] | { connectors: CustomMcpListing[] }>(
        this.base(scope),
      );
      return Array.isArray(r) ? r : (r.connectors ?? []);
    } catch (e: unknown) {
      if (e instanceof RestError && e.status === 404) return [];
      throw e;
    }
  }

  /** Add a custom MCP connector. Sends the pasted secret exactly once. */
  add(scope: McpScope, input: AddMcpInput): Promise<{ name: string }> {
    return this.client.post<{ name: string }>(this.base(scope), input);
  }

  /** Run a tools/list discovery probe. Never throws on MCP-down — the backend
   *  returns { server_up:false, tools:[] }. */
  discover(scope: McpScope, name: string): Promise<DiscoverResponse> {
    return this.client.post<DiscoverResponse>(
      `${this.base(scope)}/${encodeURIComponent(name)}/discover`,
      {},
    );
  }

  /** Cached discovered tools + current curated allowlist for the Manage-tools
   *  editor. No secret, no live probe — reads the cached discovery result. */
  getTools(scope: McpScope, name: string): Promise<CustomMcpTools> {
    return this.client.get<CustomMcpTools>(
      `${this.base(scope)}/${encodeURIComponent(name)}/tools`,
    );
  }

  /** Live re-discovery for the Manage-tools editor: probes the MCP, persists
   *  the result, busts the agent's cache, and returns the getTools shape.
   *  Company scope uses POST /tools/refresh (throws RestError 502 when the MCP
   *  is down). Personal scope has no refresh route, so it discovers then
   *  re-reads, and throws when the server is unreachable to match. */
  async refreshTools(scope: McpScope, name: string): Promise<CustomMcpTools> {
    const path = `${this.base(scope)}/${encodeURIComponent(name)}`;
    if (scope === "company") {
      return this.client.post<CustomMcpTools>(`${path}/tools/refresh`, {});
    }
    const r = await this.discover(scope, name);
    if (!r.server_up) {
      throw new RestError(502, "MCP server unreachable: could not refresh tools", path);
    }
    return this.getTools(scope, name);
  }

  /** Start MCP-native OAuth (auth_mode="mcp_oauth"). POSTs the shared connector
   *  /authorize (slug = "{name}_oauth"); the server resolves the mcp_url itself
   *  and negotiates OAuth (no custom header — the BFF proxy strips them).
   *  Returns the third-party authorize_url to open. On 409 the backend signals
   *  the server isn't OAuth-discoverable ({fallback:"manual_oauth"}). */
  connect(name: string): Promise<{ authorize_url: string }> {
    return this.client.post<{ authorize_url: string }>(
      `/plugins/v1/connectors/${encodeURIComponent(name)}_oauth/authorize`,
      {},
    );
  }

  /** Per-user static: attach the caller's own bearer/basic/custom-header
   *  secret to a company-installed static connector. Server writes to
   *  user_connector_tokens keyed on (company, slug, caller_user_id) —
   *  authorized by the userJWT, not admin. This is the paste-my-token flow
   *  the "Each user pastes their own token (per-user)" install mode expects
   *  every end-user to hit once before their first tool call. */
  attachSecret(
    name: string,
    body: {
      mode: "bearer" | "header" | "basic";
      header_name?: string;
      secret: string;
    },
  ): Promise<{ status: string; name: string }> {
    return this.client.post<{ status: string; name: string }>(
      `/plugins/v1/connectors/${encodeURIComponent(name)}/secret`,
      body,
    );
  }

  /** Persist the curated allowlist of tools to expose as skills.
   *  Backend contract is _CurateBody: { tools, annotations } — NOT curated_tools
   *  (that name only appears in listings/add input). */
  saveSkills(scope: McpScope, name: string, curated_tools: string[]): Promise<void> {
    return this.client.put<void>(
      `${this.base(scope)}/${encodeURIComponent(name)}/skills`,
      { tools: curated_tools, annotations: {} },
    );
  }

  /** Uninstall a custom MCP connector (and revoke any stored secret). */
  remove(scope: McpScope, name: string): Promise<void> {
    return this.client.delete<void>(
      `${this.base(scope)}/${encodeURIComponent(name)}`,
    );
  }

  /** Revoke the caller's OAuth token (sign out) without uninstalling — leaves
   *  the connector installed so it can be reconnected. OAuth tokens are
   *  per-user even for a company connector, so only the caller is disconnected. */
  disconnect(scope: McpScope, name: string): Promise<void> {
    return this.client.post<void>(
      `${this.base(scope)}/${encodeURIComponent(name)}/disconnect`,
      {},
    );
  }
}
